import * as React from "react"
import type {
  FieldError,
  FieldValues,
  UseFormProps,
  UseFormReturn,
} from "react-hook-form"
import {
  useForm as useHookForm,
  useFormContext as useHookFormContext,
} from "react-hook-form"

import type { z } from "zod"
import type { FetchResult } from "@apollo/client/link/core"
import { yupResolver } from "@hookform/resolvers/yup"
import { zodResolver } from "@hookform/resolvers/zod"

import type Yup from "../yup"

import type { MutationHandler } from "./useMutationHandler"
import { useMutationHandler } from "./useMutationHandler"

interface Props<T extends FieldValues> extends UseFormProps<T> {
  /**
   * @deprecated use `schema` instead.
   */
  yupSchema?: Yup.AnyObjectSchema
  schema?: z.ZodType<T, any, any>
}

/**
 * @deprecated use 'react-hook-form' directly
 */
export function useForm<T extends Record<string, any>>(props?: Props<T>) {
  const [appError, setAppError] = React.useState<string | null | undefined>()
  const mutationHandler = useMutationHandler()
  const form = useHookForm<T>({
    mode: "onSubmit",
    reValidateMode: "onChange",
    criteriaMode: "all",
    resolver: props?.schema
      ? zodResolver(props.schema)
      : props?.yupSchema && yupResolver(props.yupSchema),
    ...props,
  })

  const setFieldErrors = (errors: FieldError[]) =>
    errors.forEach((error) => form.setError(error.type as any, error))

  async function handler<T>(
    mutation: () => Promise<FetchResult<T>>,
    handler?: MutationHandler<T>,
  ) {
    setAppError(null)
    return mutationHandler(mutation, handler, { setAppError, setFieldErrors })
  }

  return {
    ...form,
    appError,
    setAppError,
    handler,
    handleSubmit: form.handleSubmit as UseFormReturn<T>["handleSubmit"],
  }
}

export type UseAppFormContextOptions = UseFormReturn<any> & {
  appError?: string
}

export function useFormContext() {
  return useHookFormContext() as UseAppFormContextOptions
}
